import '@app/App.css';
import { Button } from '@comps/ui/button';
import { Checkbox } from '@comps/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@comps/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@comps/ui/form';
import { Input } from '@comps/ui/input';
import { Label } from '@comps/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@comps/ui/select';
import { CompozzError, isCompozzError } from '@dto/CompozzError';
import { AddRightOption, allowedOptions, DashboardRightType, DeleteRightOption, DriveRightType, EditRightAccess, FullRightAccess, implicitOptionsForLevel, MembersRightType, nextOptions, NoneRightAccess, normalizeRole, ObjectRightType, ReadRightAccess, Right, RightLevel, RightOption, Role, ViewRightType, copyRole } from '@dto/Users';
import { Workspace } from '@dto/workspaces';
import { zodResolver } from '@hookform/resolvers/zod';
import { Separator } from '@radix-ui/react-select';
import { BASE_URL, DEFAULT_ORDER_INCREMENT } from '@utils/constants';
import { fetchRoles as fetchWorkspaceRoles } from '@utils/model';
import { toastError } from '@utils/toasts';
import { TW_AddRoleButton, TW_AddRoleFormButton, TW_AddRoleFormButtons, TW_AddRoleFormContent, TW_AddRoleFormError, TW_BackMembersButton, TW_BackMembersButtonIcon, TW_BackMembersButtonText, TW_DeleteRoleFormContent, TW_DeleteRoleFormError, TW_RightAccessSelectTrigger, TW_RoleDetailsContainer, TW_RoleDetailsRightContainer, TW_RoleDetailsRightContainer_WithoutDefault, TW_RoleDetailsRightDefault, TW_RoleDetailsRightLabel, TW_RoleDetailsRightOptionsContainer, TW_RoleDetailsRightRestoreButton, TW_RoleDetailsRoleNameInput, TW_RoleDetailsRoleNameLabel, TW_RoleDetailsSection, TW_RoleFormButton, TW_DeleteRoleFormButtons as TW_RoleFormButtons, TW_DeleteRoleFormConfirmation as TW_RoleFormConfirmation, TW_Roles, TW_RolesSelectContainer, TW_RolesSelectDeleteButton, TW_RolesSelectDeleteButtonIcon, TW_RolesSelectTrigger, TW_RolesSeparator, TW_RolesTitle, TW_RolesTitleContainer, getWsButtonStyle, useHover, useIconHover } from '@utils/twClasses';
import { del, post, preventDefault, put } from '@utils/utils';
import { Trash } from 'iconsax-react';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { translate } from 'src/i18n/i18n';
import { useModelStore } from 'src/store';
import { z } from 'zod';
import RightOptionChips from './RightOptionChips';
import RightOptionsMenu from './RightOptionsMenu';


function Roles() {

  const { t } = useTranslation();
  const workspace = useModelStore(state => state.selectedWorkspace);
  const wsColor = useModelStore(state => state.wsColor);
  const wsHoverColor = useModelStore(state => state.wsHoverColor);
  const wsLightColor = useModelStore(state => state.wsLightColor);
  const wsDarkColor = useModelStore(state => state.wsDarkColor);
  const [roles, setRoles] = useState<Role[]>([]);
  const [isBackButtonFocused, setIsBackButtonFocused] = useState(false);
  const navigate = useNavigate();
  const [addRoleDialogOpen, setAddRoleDialogOpen] = useState(false);
  const [deleteRoleDialogOpen, setDeleteRoleDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [modifiedRole, setModifiedRole] = useState<Role | null>(null);
  const [isRoleModified, setIsRoleModified] = useState(false);
  const [switchToRoleDialogOpen, setSwitchToRoleDialogOpen] = useState<string | null>(null);

  const hoverAddButton = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
  const hoverSaveButton = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
  const hoverRestoreButton = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
  const hoverDeleteButton = useIconHover("grey", "red");

  useEffect(() => {
    fetchRoles();
  }, [workspace]);

  if (!workspace?._key) {
    console.error("Workspace key is not set");
    return;
  }

  function fetchRoles() {
    if (!workspace?._key) {
      console.error("Workspace key is not set");
      return;
    }
    // The shared loader normalises the rights (legacy access values become a level + options)
    fetchWorkspaceRoles(workspace._key, null, null, setRoles);
  }

  const onRoleAdded = function onRoleAdded(role: Role) {
    const newRoles = [...roles, role];
    setRoles(newRoles);
    setAddRoleDialogOpen(false);
    fetchRoles();
  }

  const onRoleDeleted = function onRoleDeleted(role: Role) {
    const newRoles = roles.filter(r => r._key !== role._key);
    setRoles(newRoles);
    setSelectedRole(null);
    setModifiedRole(null);
    setIsRoleModified(false);
    setDeleteRoleDialogOpen(false);
    fetchRoles();
  }


  function switchToRole(roleKey: string) {
    const role = roles.find(r => r._key === roleKey);
    if (role) {
      setSelectedRole(copyRole(role));
      setModifiedRole(copyRole(role));
    } else {
      setSelectedRole(null);
      setModifiedRole(null);
    }
  }

  const onRoleSelected = function onRoleSelected(roleKey: string) {
    if (!isRoleModified) {
      switchToRole(roleKey);
    } else {
      setSwitchToRoleDialogOpen(roleKey);
    }
  }

  const confirmCancelModif = function confirmCancelModif() {
    if (switchToRoleDialogOpen) {
      switchToRole(switchToRoleDialogOpen);
      setSwitchToRoleDialogOpen(null);
      setIsRoleModified(false);
    }
  }

  const onRoleModification = function onRoleModification(role: Role) {
    setModifiedRole(role);
    setIsRoleModified(true);
  }

  const restoreRole = function restoreRole() {
    if (modifiedRole) {
      if (selectedRole) {
        setModifiedRole(copyRole(selectedRole));
      } else {
        setModifiedRole(null);
      }
      setIsRoleModified(false);
    }
  }

  const updateRole = function updateRole() {
    if (!workspace?._key) {
      return;
    }

    if (modifiedRole && selectedRole) {
      put<Role>(`${BASE_URL}/model/workspaces/${workspace._key}/roles/${selectedRole._key}`, modifiedRole).then(response => {
        if (response) {
          if (isCompozzError(response)) {
            const cError = response as CompozzError;
            if (cError.i18n) {
              toastError(translate(t, cError.i18n, cError.params));
            } else {
              toastError(cError.message);
            }
          } else {
            const newRole = normalizeRole(response as Role);
            const newRoles = roles.map(r => r._key === newRole._key ? newRole : r);
            setRoles(newRoles);
            setSelectedRole(copyRole(newRole));
            setModifiedRole(copyRole(newRole));
            setIsRoleModified(false);
          }
        }
      });
    }
  }

  const scrollStyle = {
    scrollbarColor: wsColor + " " + wsLightColor
  }

  const nextOrder = (roles.length > 0) ? roles[roles.length - 1].order + DEFAULT_ORDER_INCREMENT : DEFAULT_ORDER_INCREMENT;
  return (
    <section className={TW_Roles}>
      <button className={TW_BackMembersButton}
        onClick={() => navigate(`/workspaces/${workspace._key}/members`)}
        autoFocus={false}
        onMouseEnter={() => setIsBackButtonFocused(true)}
        onMouseLeave={() => setIsBackButtonFocused(false)}
        style={{
          backgroundColor: isBackButtonFocused ? wsHoverColor : wsLightColor,
          border: `1px solid ${wsColor}`,
        }}
      >
        <ArrowLeft className={TW_BackMembersButtonIcon} />
        <span className={TW_BackMembersButtonText}>{t('roles.button.back.members')}</span>
      </button>

      <div className={TW_RolesTitleContainer}>
        <h1 className={TW_RolesTitle}>{t('roles.title')}</h1>
        <Dialog open={addRoleDialogOpen} onOpenChange={setAddRoleDialogOpen}>
          <DialogTrigger><p className={TW_AddRoleButton} {...hoverAddButton} >{t('roles.add.button')}</p></DialogTrigger>
          <DialogContent className={TW_AddRoleFormContent}>
            <DialogHeader>
              <DialogTitle>{t("roles.add.form.title")}</DialogTitle>
            </DialogHeader>
            <AddRoleForm nextOrder={nextOrder} workspace={workspace} onCancel={() => setAddRoleDialogOpen(false)} onRoleAdded={onRoleAdded} />
          </DialogContent>
        </Dialog>
      </div>
      <div className={TW_RolesSelectContainer}>
        <Dialog open={switchToRoleDialogOpen !== null} onOpenChange={() => setSwitchToRoleDialogOpen(null)}>
          <DialogTrigger asChild>
            <Select value={selectedRole?._key} onValueChange={onRoleSelected}>
              <SelectTrigger className={TW_RolesSelectTrigger}>
                <SelectValue placeholder={t('roles.select.placeholder')} />
              </SelectTrigger>
              <SelectContent>
                {roles.map(role => {
                  return (
                    <SelectItem key={role._key} value={role._key}>{role.name}</SelectItem>
                  )
                })}
              </SelectContent>
            </Select>
          </DialogTrigger>
          <DialogContent className={TW_DeleteRoleFormContent}>
            <DialogHeader>
              <DialogTitle>{t("roles.select.cancel.modif.title")}</DialogTitle>
            </DialogHeader>
            <CancelModifConfirmation onCancel={() => setSwitchToRoleDialogOpen(null)} onConfirm={confirmCancelModif} />
          </DialogContent>
        </Dialog>
        <Dialog open={deleteRoleDialogOpen} onOpenChange={setDeleteRoleDialogOpen}>
          <DialogTrigger className={TW_RolesSelectDeleteButton} disabled={!selectedRole}><Trash className={TW_RolesSelectDeleteButtonIcon} {...hoverDeleteButton} /></DialogTrigger>
          <DialogContent className={TW_DeleteRoleFormContent}>
            <DialogHeader>
              <DialogTitle>{t("roles.delete.form.title")}</DialogTitle>
            </DialogHeader>
            <DeleteRoleForm workspace={workspace} role={selectedRole ?? null} onCancel={() => setDeleteRoleDialogOpen(false)} onRoleDeleted={onRoleDeleted} />
          </DialogContent>
        </Dialog>
      </div>
      <Separator className={TW_RolesSeparator} />
      {/* <div className={TW_RoleDetailsContainer}> */}
      <div className={TW_RoleDetailsContainer} style={scrollStyle}>
        {modifiedRole && <RoleDetails role={modifiedRole} workspace={workspace} onRoleModification={onRoleModification} />}
        {selectedRole && <div className={TW_RoleFormButtons}>
          <Button className={TW_RoleFormButton} {...hoverRestoreButton} onClick={restoreRole} disabled={!isRoleModified}>{t("roles.button.restore")}</Button>
          <Button className={TW_RoleFormButton + " mr-2"} {...hoverSaveButton} onClick={updateRole} disabled={!isRoleModified}>{t("roles.button.update")}</Button>
        </div>}
      </div>
      {/* </div> */}
    </section>
  );
};

interface RoleDetailsProps {
  role: Role | null;
  workspace: Workspace | null;
  onRoleModification: (role: Role) => void;
}

// Options carried by a right that has no options UI when it is (re)created
// at the FULL level: add and delete, as the former FULL_WITH_DELETE value did.
const FULL_DEFAULT_OPTIONS: RightOption[] = [AddRightOption, DeleteRightOption];

function RoleDetails({ role, workspace, onRoleModification }: RoleDetailsProps) {
  const { t } = useTranslation();
  const [roleName, setRoleName] = useState(role?.name);
  const objectInfos = useModelStore(state => state.objectInfos);
  const isDrive = workspace?.drive === true;

  useEffect(() => {
    setRoleName(role?.name);
  }, [role]);


  let objectRight = role?.rights.find(right => right.type === ObjectRightType && !right.recordKey);
  let dashboardsRight = role?.rights.find(right => right.type === DashboardRightType && !right.recordKey);
  let membersRight = role?.rights.find(right => right.type === MembersRightType && !right.recordKey);
  let viewsRight = role?.rights.find(right => right.type === ViewRightType && !right.recordKey);
  let driveRight = role?.rights.find(right => right.type === DriveRightType && !right.recordKey);

  if (!objectRight) {
    objectRight = {
      type: ObjectRightType,
      access: FullRightAccess,
      options: [...FULL_DEFAULT_OPTIONS],
      recordKey: null,
      active: false,
    };
  }

  if (!dashboardsRight) {
    dashboardsRight = {
      type: DashboardRightType,
      access: FullRightAccess,
      options: [...FULL_DEFAULT_OPTIONS],
      recordKey: null,
      active: false,
    };
  }

  if (!membersRight) {
    membersRight = {
      type: MembersRightType,
      access: FullRightAccess,
      options: [...FULL_DEFAULT_OPTIONS],
      recordKey: null,
      active: false,
    };
  }

  if (!viewsRight) {
    viewsRight = {
      type: ViewRightType,
      access: FullRightAccess,
      options: [...FULL_DEFAULT_OPTIONS],
      recordKey: null,
      active: false,
    };
  }

  if (!driveRight) {
    driveRight = {
      type: DriveRightType,
      access: NoneRightAccess,
      options: [],
      recordKey: null,
      active: false,
    };
  }

  const objectRights = role?.rights.filter(right => right.type === ObjectRightType && right.recordKey !== null);
  const objectRightByRecordKey = new Map<string, Right>();
  objectRights?.forEach(right => {
    objectRightByRecordKey.set(right.recordKey!, right);
  });

  const onRightChange = function onRightChange(right: Right) {
    if (!role) {
      return;
    }
    let rightFound = false;
    const newRights = role?.rights.map(r => {
      if (r.type === right.type && r.recordKey === right.recordKey) {
        rightFound = true;
        return right;
      }
      return r;
    });
    if (!rightFound) {
      newRights.push(right);
    }
    const newRole: Role = { ...role, rights: newRights };
    onRoleModification(newRole);
  }

  const onNameChange = function onNameChange(name: string) {
    if (!role) {
      return;
    }
    setRoleName(name);
    const newRole: Role = { ...role, name: name };
    onRoleModification(newRole);
  }

  const restoreDefault = function restoreDefault(right: Right) {
    if (role && right.recordKey) {
      const newRights = role.rights.map(r => {
        if (r.type === right.type && r.recordKey === right.recordKey) {
          return null;
        }
        return r;
      }).filter(r => r !== null) as Right[];
      const newRole: Role = { ...role, rights: newRights };
      onRoleModification(newRole);
    }
  }

  const objectPossibleRights: RightLevel[] = [FullRightAccess, EditRightAccess, ReadRightAccess, NoneRightAccess];
  const dashboardsPossibleRights: RightLevel[] = [FullRightAccess, ReadRightAccess, NoneRightAccess]
  const membersPossibleRights: RightLevel[] = [FullRightAccess, NoneRightAccess];
  const viewsPossibleRights: RightLevel[] = [FullRightAccess, NoneRightAccess];
  const drivePossibleRights: RightLevel[] = [ReadRightAccess, NoneRightAccess];

  if (isDrive) {
    return (
      <>
        <div className={TW_RoleDetailsSection}>
          <Label className={TW_RoleDetailsRoleNameLabel}>{t("roles.details.role.name.label")}</Label>
          <Input className={TW_RoleDetailsRoleNameInput} value={roleName} onChange={(e) => onNameChange(e.target.value)} />
        </div>
        <div className={TW_RoleDetailsSection}>
          <Label className={TW_RoleDetailsRoleNameLabel}>{t("roles.details.general.rights.label")}</Label>
          <RoleRight label={t("roles.details.general.rights.drive.label")} right={driveRight} onChange={onRightChange} isDefault={null} restoreDefault={null} possibleRights={drivePossibleRights} withOptions={false} />
          <RoleRight label={t("roles.details.general.rights.members.label")} right={membersRight} onChange={onRightChange} isDefault={null} restoreDefault={null} possibleRights={membersPossibleRights} withOptions={false} />
        </div>
      </>
    );
  }

  return (
    <>
      <div className={TW_RoleDetailsSection}>
        <Label className={TW_RoleDetailsRoleNameLabel}>{t("roles.details.role.name.label")}</Label>
        <Input className={TW_RoleDetailsRoleNameInput} value={roleName} onChange={(e) => onNameChange(e.target.value)} />
      </div>
      <div className={TW_RoleDetailsSection}>
        <Label className={TW_RoleDetailsRoleNameLabel}>{t("roles.details.general.rights.label")}</Label>
        <RoleRight label={t("roles.details.general.rights.objects.label")} right={objectRight} onChange={onRightChange} isDefault={null} restoreDefault={null} possibleRights={objectPossibleRights} withOptions={true} />
        <RoleRight label={t("roles.details.general.rights.dashboards.label")} right={dashboardsRight} onChange={onRightChange} isDefault={null} restoreDefault={null} possibleRights={dashboardsPossibleRights} withOptions={false} />
        <RoleRight label={t("roles.details.general.rights.members.label")} right={membersRight} onChange={onRightChange} isDefault={null} restoreDefault={null} possibleRights={membersPossibleRights} withOptions={false} />
        <RoleRight label={t("roles.details.general.rights.views.label")} right={viewsRight} onChange={onRightChange} isDefault={null} restoreDefault={null} possibleRights={viewsPossibleRights} withOptions={false} />
      </div>
      <div className={TW_RoleDetailsSection}>
        <Label className={TW_RoleDetailsRoleNameLabel}>{t("roles.details.object.rights.label")}</Label>
        {objectInfos.map(info => {
          let right = objectRightByRecordKey.get(info.object._key);
          let isDefault = !right;
          if (!right) {
            // Mirrors the default object right (level and options) until the object gets its own right
            right = {
              type: ObjectRightType,
              access: objectRight?.access ?? FullRightAccess,
              options: [...(objectRight?.options ?? FULL_DEFAULT_OPTIONS)],
              recordKey: info.object._key,
              active: true,
            };
          }
          return (
            <RoleRight key={"object-right-" + right.recordKey} label={info.object.name} right={right} onChange={onRightChange} isDefault={isDefault} restoreDefault={restoreDefault} possibleRights={objectPossibleRights} withOptions={true} />
          )
        })}
      </div>
    </>
  );
}

interface RoleRightProps {
  label: string;
  right: Right;
  onChange: (right: Right) => void;
  isDefault: boolean | null;
  restoreDefault: ((right: Right) => void) | null;
  possibleRights: RightLevel[];
  /**
   * Shows the options of the right (chips + "Options…" menu). Rows without it
   * (dashboards, members, views, drive) carry the implicit options of their level.
   */
  withOptions: boolean;
}

function RoleRight({ label, right, onChange, isDefault, restoreDefault, possibleRights, withOptions }: RoleRightProps) {
  const { t } = useTranslation();
  const wsColor = useModelStore(state => state.wsColor);
  const options = right.options ?? [];

  const handleCheckedChange = function handleCheckedChange(checked: boolean) {
    const newRight = { ...right, active: checked };
    onChange(newRight);
  }

  const handleAccessChange = function handleAccessChange(access: string) {
    const level = access as RightLevel;
    // Level change: climbing to EDIT / FULL turns add + delete on, export is
    // never turned on by itself but kept when already checked, and the options
    // the new level does not allow are dropped (see nextOptions).
    const newOptions = withOptions ? nextOptions(right.access as RightLevel, level, options) : implicitOptionsForLevel(level);
    const newRight: Right = { ...right, access: level, options: newOptions };
    onChange(newRight);
  }

  const handleOptionsChange = function handleOptionsChange(newOptions: RightOption[]) {
    const newRight: Right = { ...right, options: allowedOptions(right.access, newOptions) };
    onChange(newRight);
  }

  const handleRestoreDefault = function handleRestoreDefault(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    if (restoreDefault) {
      restoreDefault(right);
    }
  }

  const menuIdPrefix = `right-${right.type}-${right.recordKey ?? "default"}`;

  return (
    <div className={TW_RoleDetailsRightContainer}>
      <div className={TW_RoleDetailsRightContainer_WithoutDefault}>
        <Checkbox className="mt-2.5 " checked={right.active} onCheckedChange={handleCheckedChange} style={right.active ? { backgroundColor: wsColor, borderColor: wsColor } : {}} />
        <Label className={TW_RoleDetailsRightLabel + (right.active ? "" : " opacity-50")}>{label}</Label>
        <div className="flex flex-col justify-start items-start gap-2">
          <Select value={right.access} onValueChange={handleAccessChange} disabled={!right.active}>
            <SelectTrigger className={TW_RightAccessSelectTrigger}>
              <SelectValue placeholder={t('rights.access.select.placeholder')} />
            </SelectTrigger>
            <SelectContent>
              {possibleRights.map(access => {
                return (
                  <SelectItem key={access} value={access}>{t(`right.access.value.${access}`)}</SelectItem>
                )
              })}
            </SelectContent>
          </Select>
          {withOptions &&
            <div className={TW_RoleDetailsRightOptionsContainer + (right.active ? "" : " opacity-50")}>
              <RightOptionChips level={right.access} options={options} />
              <RightOptionsMenu idPrefix={menuIdPrefix} level={right.access} options={options} onChange={handleOptionsChange} disabled={!right.active} />
            </div>
          }
        </div>
      </div>

      {isDefault && <p className={TW_RoleDetailsRightDefault + (right.active ? "" : " opacity-50")}>{t("roles.details.right.default")}</p>}
      {!isDefault && restoreDefault &&
        <Button className={TW_RoleDetailsRightRestoreButton + (right.active ? " hover:text-gray-800" : " opacity-50")} onClick={handleRestoreDefault} disabled={!right.active}>

          {t("roles.details.right.restore")}
          {/* <Back className={TW_RoleDetailsRightRestoreButtonIcon} /> */}
        </Button>}
    </div>
  );
}

interface AddRoleFormProps {
  nextOrder: number;
  workspace: Workspace | null;
  onCancel: () => void;
  onRoleAdded: (role: Role) => void;
}

function AddRoleForm({ nextOrder, workspace, onCancel, onRoleAdded }: AddRoleFormProps) {
  const { t } = useTranslation();
  const [error, setError] = useState("");


  const formSchema = z.object({
    name: z.string().min(1, {
      message: t("roles.add.form.error.name"),
    }),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
    },
  })

  const addRole = function addRole() {
    const isDrive = workspace?.drive === true;
    // A new role gets the full access with add + delete (never export, which is
    // an explicit choice of the administrator).
    const newRole = {
      name: form.getValues("name"),
      order: nextOrder,
      rights: isDrive
        ? [
            {
              type: DriveRightType,
              access: ReadRightAccess,
              options: [],
              recordKey: null,
              active: true,
            },
            {
              type: MembersRightType,
              access: FullRightAccess,
              options: [...FULL_DEFAULT_OPTIONS],
              recordKey: null,
              active: true,
            },
          ]
        : [
            {
              type: ObjectRightType,
              access: FullRightAccess,
              options: [...FULL_DEFAULT_OPTIONS],
              recordKey: null,
              active: true,
            },
            {
              type: DashboardRightType,
              access: FullRightAccess,
              options: [...FULL_DEFAULT_OPTIONS],
              recordKey: null,
              active: true,
            },
            {
              type: MembersRightType,
              access: FullRightAccess,
              options: [...FULL_DEFAULT_OPTIONS],
              recordKey: null,
              active: true,
            },
            {
              type: ViewRightType,
              access: FullRightAccess,
              options: [...FULL_DEFAULT_OPTIONS],
              recordKey: null,
              active: true,
            },
          ],
    };
    post<Role>(`${BASE_URL}/model/workspaces/${workspace?._key}/roles`, newRole).then(response => {
      if (response) {
        if (isCompozzError(response)) {
          const cError = response as CompozzError;
          if (cError.i18n) {
            setError(translate(t, cError.i18n, cError.params));
          } else {
            setError(cError.message);
          }
        } else {
          onRoleAdded(normalizeRole(response as Role));
          setError("");
        }
      }
    });
  }


  const onKeyDown = function onKeyDown(e: React.KeyboardEvent<HTMLFormElement>) {
    e.stopPropagation();
    if (e.key === 'Enter') {
      form.handleSubmit(addRole)();
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={preventDefault} className="space-y-8 w-full mt-6" onKeyDown={onKeyDown}>
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem aria-required>
              <FormLabel>{t("roles.add.form.name")}</FormLabel>
              <FormControl>
                <Input placeholder={t("roles.add.form.name.placeholder")} {...field} required />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className={TW_AddRoleFormButtons}>
          <div className={TW_AddRoleFormError}>{error}</div>
          <Button className={TW_AddRoleFormButton} onClick={onCancel}>{t("general.button.cancel.text")}</Button>
          <Button className={TW_AddRoleFormButton} onClick={form.handleSubmit(addRole)}>{t("roles.add.form.add")}</Button>
        </div>
      </form>
    </Form>
  )
}

interface DeleteRoleFormProps {
  workspace: Workspace | null;
  role: Role | null;
  onCancel: () => void;
  onRoleDeleted: (role: Role) => void;
}

function DeleteRoleForm({ workspace, role, onCancel, onRoleDeleted }: DeleteRoleFormProps) {
  const { t } = useTranslation();
  const [error, setError] = useState("");
  const wsColor = useModelStore(state => state.wsColor);
  const wsDarkColor = useModelStore(state => state.wsDarkColor);
  const cancelHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
  const confirmHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));

  if (!role) {
    return null;
  }

  const onConfirm = function onConfirm() {
    del<void>(`${BASE_URL}/model/workspaces/${workspace?._key}/roles/${role._key}`, {}).then(response => {
      if (response) {
        if (isCompozzError(response)) {
          const cError = response as CompozzError;
          if (cError.i18n) {
            setError(translate(t, cError.i18n, cError.params));
          } else {
            setError(cError.message);
          }
        }
      } else {
        onRoleDeleted(role);
        setError("");
      }
    });
  }

  return (
    <>
      <p className={TW_RoleFormConfirmation}>{translate(t, "roles.delete.form.confirmation", [role.name])}</p>
      <div className={TW_RoleFormButtons}>
        <div className={TW_DeleteRoleFormError}>{error}</div>
        <Button className={TW_RoleFormButton} onClick={onCancel} {...cancelHover}>{t("general.button.cancel.text")}</Button>
        <Button className={TW_RoleFormButton} onClick={onConfirm} {...confirmHover}>{t("general.button.confirm.text")}</Button>
      </div>
    </>
  )
}

interface CancelModifConfirmationProps {
  onCancel: () => void;
  onConfirm: () => void;
}

function CancelModifConfirmation({ onCancel, onConfirm }: CancelModifConfirmationProps) {
  const { t } = useTranslation();
  const wsColor = useModelStore(state => state.wsColor);
  const wsDarkColor = useModelStore(state => state.wsDarkColor);
  const cancelHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
  const confirmHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));

  return (
    <>
      <p className={TW_RoleFormConfirmation}>{t("roles.select.cancel.modif.confirmation")}</p>
      <div className={TW_RoleFormButtons}>
        <Button className={TW_RoleFormButton} onClick={onCancel} {...cancelHover}>{t("general.button.cancel.text")}</Button>
        <Button className={TW_RoleFormButton} onClick={onConfirm} {...confirmHover}>{t("general.button.confirm.text")}</Button>
      </div>
    </>
  )
}

export default Roles;
